import React, { useEffect, useState, useRef, useMemo } from 'react';
import produce from 'immer';
import { message, Tree } from 'antd';
import { AntTreeNode, AntTreeNodeCheckedEvent } from 'antd/lib/tree/Tree';
import { arrayWrap } from 'src/utils/arrayWrap';
import { isArray } from 'src/utils/typeGuards';
import TreeProps from '../../types/TreeProps';
import { getLookupCategories } from '../../services/productAssociation.service';
import TreeNodeProps from '../../types/TreeNodeProps';
import styles from './LookupOptions.module.less';

const { TreeNode } = Tree;

type CheckedKeys = {
  checked: string[];
  halfChecked: string[];
};

type CategoriesProps = {
  data: TreeNodeProps[];
  currentValue: string[];
  currentSourceSite?: string | null;
  onChange: (val: string[]) => void;
};

const splitPath = (str: string) => str.split(':');
const joinPaths = (paths: string[]) => paths.join(':');

/**
 * transforms ["1:2:3", "4:5:6", ...] into `antd Tree` format { "checked": [...], "halfChecked": [...] }
 */
const transformToCheckedKeys = (value: string[]): CheckedKeys => {
  const checked = new Set<string>();
  const halfChecked = new Set<string>();
  value.forEach((path) => {
    const [last, ...others] = splitPath(path).reverse();
    others.forEach((id) => halfChecked.add(id));
    checked.add(last);
  });
  checked.forEach((id) => halfChecked.delete(id));
  return { checked: [...checked], halfChecked: [...halfChecked] };
};

/**
 * transforms `antd Tree` format { "checked": [...],  "halfChecked": [...] } back into ["1:2:3", "4:5:6", ...]
 */
const transformToPaths = (selected: string[], selectedPaths: string[], pathsMap: Map<string, string>): string[] => {
  const map = new Map(selectedPaths.map((path) => [splitPath(path).slice(-1)[0], path]));
  return selected.flatMap((id) => {
    const path = map.get(id) ?? pathsMap.get(id);
    if (!path) {
      console.error(`Path for id: ${id} is not found in the both maps`);
      return [];
    }
    return path;
  });
};

const getTreeNode = (treeNodes: TreeNodeProps[], treeNodeId: string): TreeNodeProps | undefined => {
  for (const node of treeNodes) {
    if (node.id === treeNodeId) {
      return node;
    }
    if (node.children) {
      const childNode = getTreeNode(node.children, treeNodeId);
      if (childNode) {
        return childNode;
      }
    }
  }
};

const getTreeNodeParents = (treeNode: TreeNodeProps): TreeNodeProps[] => {
  const parents: TreeNodeProps[] = arrayWrap(treeNode.parent);
  return parents.concat(parents.flatMap(getTreeNodeParents));
};

const getTreeNodeChildren = (treeNode: TreeNodeProps): TreeNodeProps[] => {
  const children: TreeNodeProps[] = treeNode.children ?? [];
  return children.concat(children.flatMap(getTreeNodeChildren));
};

const getTreeNodesPaths = (treeNodes: TreeNodeProps[], parentPath?: string): [string, string][] => {
  return treeNodes.map(({ id }) => {
    const fullPath = parentPath ? joinPaths([parentPath, id]) : id;
    return [id, fullPath];
  });
};

const renderTreeNodes = (data: TreeNodeProps[]) =>
  data.map((item: TreeNodeProps) => {
    const { children, ...props } = item;
    return (
      <TreeNode {...props} key={props.id} dataRef={item}>
        {children && renderTreeNodes(children)}
      </TreeNode>
    );
  });

export const Categories: React.FC<CategoriesProps> = ({ data, currentValue, currentSourceSite, onChange }) => {
  const [treeData, setTreeData] = useState<TreeNodeProps[]>(data);
  const { checked, halfChecked } = useMemo(() => transformToCheckedKeys(currentValue), [currentValue]);
  const pathsMapRef = useRef(new Map<string, string>());

  useEffect(() => {
    pathsMapRef.current = new Map(getTreeNodesPaths(data));
    setTreeData(data);
  }, [data]);

  const setTreeNodeChildren = (treeNodeId: string, children: TreeNodeProps[]) => {
    setTreeData(
      produce((draft: TreeNodeProps[]) => {
        const node = getTreeNode(draft, treeNodeId);
        if (!node) {
          console.error(`TreeNode with id: ${treeNodeId} is not found while trying to set children`);
          return;
        }
        node.children = children.map((child) => ({ ...child, parent: node }));

        const map = pathsMapRef.current;
        const paths = getTreeNodesPaths(node.children, map.get(node.id) ?? node.id);
        paths.forEach(([id, path]) => map.set(id, path));
      })
    );
  };

  const handleChange = (checkedKeys: TreeProps.CheckedKeys, e: AntTreeNodeCheckedEvent) => {
    if (!isArray(checkedKeys)) {
      const treeNode = e.node.props.dataRef;
      const allParents = getTreeNodeParents(treeNode);
      const allChildren = getTreeNodeChildren(treeNode);
      const allParentsIds = allParents.map(({ id }) => id);
      const allChildrenIds = new Set(allChildren.map(({ id }) => id));
      const removedPaths = [treeNode.id, ...allParentsIds].reverse().map((_, i, arr) => joinPaths(arr.slice(0, i + 1)));
      const treeNodePath = removedPaths.slice(-1)[0];

      const currentPaths = currentValue
        .filter((path) => !removedPaths.includes(path))
        .filter((path) => !path.startsWith(treeNodePath + ':'));

      const { checked } = transformToCheckedKeys(currentPaths);
      const selected = e.checked
        ? new Set([treeNode.id, ...checked, ...allChildrenIds])
        : new Set(checked.filter((id) => !allChildrenIds.has(id)));

      allParents.forEach((parent) => {
        const children = parent.children ?? [];
        const selectedChildren = children.filter(({ id }) => selected.has(id));
        if (selectedChildren.length === 0) {
          selected.delete(parent.id);
        } else if (selectedChildren.length === children.length) {
          selected.add(parent.id);
        } else {
          selected.delete(parent.id);
        }
      });

      onChange(transformToPaths([...selected], currentPaths, pathsMapRef.current));
    }
  };

  const handleLoadData = async ({ props }: AntTreeNode) => {
    if (props.children) {
      return;
    }
    try {
      const categories = await getLookupCategories({
        sourceOwnerId: currentSourceSite || '',
        categoryId: props.id,
      });
      setTreeNodeChildren(props.id, categories);
      if (props.checked) {
        const childrenIds = categories.map(({ id }) => id);
        onChange(transformToPaths([...checked, ...childrenIds], currentValue, pathsMapRef.current));
      }
    } catch {
      message.error('Categories weren`t loaded.');
    }
  };

  return (
    <div className={styles.selectTreeWrapper}>
      {data.length > 0 && (
        <Tree
          selectable={false}
          checkable
          showLine
          onCheck={handleChange}
          checkedKeys={{ checked, halfChecked }}
          checkStrictly={true}
          loadData={handleLoadData}
        >
          {renderTreeNodes(treeData)}
        </Tree>
      )}
    </div>
  );
};
